"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebSocketClient = exports.DEFAULT_ENDPOINT_URL = exports.DEFAULT_PATH = exports.DEFAULT_ERRORS = void 0;
const socket_io_client_1 = __importDefault(require("socket.io-client"));
const js_access_token_lib_1 = require("@bynder/js-access-token-lib");
const uuid_1 = require("uuid");
exports.DEFAULT_ERRORS = {
    notConnected: new Error('Not connected with WS server'),
    alreadySubscribed: new Error('Cannot subscribe to one event twice'),
};
const CONNECTING_TIMEOUT_MS = 5 * 1000; // 5 seconds
exports.DEFAULT_PATH = '/v7/socket.io.v2';
exports.DEFAULT_ENDPOINT_URL = '/ws';
class WebSocketClient {
    socket;
    state;
    constructor() {
        this.state = {
            rooms: {},
            error: exports.DEFAULT_ERRORS.notConnected,
        };
    }
    getWebSocketState() {
        return this.state;
    }
    listenEvent(eventName, callbacks) {
        this.socket.on(eventName, (JSONresponse) => {
            try {
                const response = JSON.parse(JSONresponse);
                callbacks.onSuccess(response);
            }
            catch (error) {
                callbacks.onFailure(error);
            }
        });
    }
    onRoomMessage(events, callbacks) {
        if (typeof events === 'string') {
            this.listenEvent(events, callbacks);
        }
        else {
            events.forEach((event) => {
                this.listenEvent(event, callbacks);
            });
        }
    }
    subscribe(entityName, aggregateId, eventName, callbacks, correlationId, hasCorrespondingAggregateId) {
        try {
            if (this.state.error) {
                throw this.state.error;
            }
            const roomName = this.generateRoomName(entityName, aggregateId);
            // Check if subscription to event within room already exists
            if (this.state.rooms[roomName] && this.state.rooms[roomName][eventName]) {
                throw exports.DEFAULT_ERRORS.alreadySubscribed;
            }
            this.state.rooms[roomName][eventName] = eventName;
            // Listen to event
            this.socket.on(eventName, (JSONresponse) => {
                const response = JSON.parse(JSONresponse);
                if (hasCorrespondingAggregateId) {
                    if (response.aggregate_root_id === aggregateId) {
                        callbacks.onSuccess(response);
                    }
                }
                else if (!correlationId || correlationId === response.correlationId) {
                    callbacks.onSuccess(response);
                }
            });
        }
        catch (error) {
            callbacks.onFailure(error);
        }
    }
    promisifySubscribe = (entityName, aggregateId, eventName, correlationId, hasCorrespondingAggregateId) => new Promise((resolve, reject) => this.subscribe(entityName, aggregateId, eventName, { onSuccess: resolve, onFailure: reject }, correlationId, hasCorrespondingAggregateId));
    unsubscribe = (roomName, eventName) => {
        if (this.socket !== undefined) {
            this.socket.off(eventName);
        }
        if (this.state.rooms[roomName][eventName]) {
            delete this.state.rooms[roomName][eventName];
        }
    };
    async subscribeOnce(entityName, aggregateId, eventName, callbacks, correlationId, timeoutSeconds) {
        try {
            const response = await Promise.race([
                this.promisifySubscribe(entityName, aggregateId, eventName, correlationId),
                this.promisifyTimeout(timeoutSeconds),
            ]);
            this.leaveRoom(this.generateRoomName(entityName, aggregateId));
            if (!response || response instanceof Error) {
                throw response instanceof Error
                    ? response
                    : new Error('Websocket response invalid');
            }
            callbacks.onSuccess(response);
        }
        catch (error) {
            callbacks.onFailure(error);
        }
    }
    waitForConnect = () => new Promise((resolve) => {
        if (this.socket.connected) {
            resolve('connected');
        }
        this.socket.on('connect', () => resolve('connected'));
    });
    joinRoom = (roomName) => new Promise((resolve, reject) => {
        if (this.state.error) {
            reject(this.state.error);
        }
        if (roomName in this.state.rooms) {
            resolve(`already joined ${roomName}`);
        }
        else {
            this.state.rooms[roomName] = {};
            this.socket.emit('join', roomName);
            this.socket.on(`joined ${roomName}`, () => resolve(`joined ${roomName}`));
        }
    });
    leaveRoom = (roomName) => new Promise((resolve) => {
        if (roomName in this.state.rooms) {
            this.socket.emit('leave', roomName, () => resolve(`left: ${roomName}`));
            delete this.state.rooms[roomName];
        }
        resolve('not in room');
    });
    generateRoomName = (entityName, aggregateId) => aggregateId ? `${entityName}_${aggregateId}` : entityName;
    async initialize(wsEndpointUrl, path, withToken) {
        try {
            const accessToken = withToken && (await (0, js_access_token_lib_1.getJWT)());
            const aid = (0, uuid_1.v4)();
            const options = {
                timeout: CONNECTING_TIMEOUT_MS,
                ...(withToken && {
                    auth: {
                        token: accessToken, // JWT string
                    },
                }),
                path,
                extraHeaders: {
                    'x-socket-io-affinity-id': aid,
                },
                query: {
                    aid,
                },
                reconnectionDelay: 2000,
                reconnectionDelayMax: 60000,
                randomizationFactor: 2,
            };
            this.socket = (0, socket_io_client_1.default)(wsEndpointUrl, options);
            await this.setupDefaultListeners();
        }
        catch (error) {
            this.state.error = error;
        }
    }
    setupDefaultListeners() {
        return new Promise((resolve) => {
            this.socket.on('reconnect', () => {
                Object.keys(this.state.rooms).forEach((room) => {
                    this.socket.emit('join', room);
                });
                this.state.error = null;
            });
            this.socket.on('disconnect', () => {
                this.state.error = exports.DEFAULT_ERRORS.notConnected;
            });
            // TODO: check if it's an authorization error
            // and if so, get a new access token.
            this.socket.on('error', (error) => {
                if (error) {
                    console.error(error);
                }
            });
            this.socket.on('connect', () => {
                // Rejoin existing rooms if exist
                Object.keys(this.state.rooms).forEach((room) => {
                    this.socket.emit('join', room);
                });
                this.state.error = null;
                resolve('connected');
            });
        });
    }
    promisifyTimeout = (seconds) => new Promise((resolve) => setTimeout(() => resolve(new Error('timeout')), seconds * 1000));
}
exports.WebSocketClient = WebSocketClient;
// Only when no callback is passed, it returns a promise.
const getWebsocketClient = (wsEndpointUrl = exports.DEFAULT_ENDPOINT_URL, path = exports.DEFAULT_PATH, optionalCallback, withToken = true) => {
    if (typeof optionalCallback === 'function') {
        if (window.WebSocketClient) {
            optionalCallback(window.WebSocketClient);
        }
        else {
            window.WebSocketClient = new WebSocketClient();
            window.WebSocketClient.initialize(wsEndpointUrl, path, withToken).then(() => {
                optionalCallback(window.WebSocketClient);
            });
        }
    }
    else {
        return new Promise((resolve) => {
            if (window.WebSocketClient) {
                resolve(window.WebSocketClient);
            }
            else {
                window.WebSocketClient = new WebSocketClient();
                window.WebSocketClient.initialize(wsEndpointUrl, path, withToken).then(() => {
                    resolve(window.WebSocketClient);
                });
            }
        });
    }
};
exports.default = getWebsocketClient;
//# sourceMappingURL=WebSocketClient.js.map